<script lang="ts">
	import { smartContractWalletAddress } from '$lib/home/scwallet/store';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { addDollarSign, getNumBackInUnits } from '$lib/util/util';
	import { smartWalletCreationPending, walletConnected, walletLoaded } from '$lib/wallet/store';
	import BigNumber from 'bignumber.js';
	import Chart from './Chart.svelte';
	import ChartRangeOptionItem from './ChartRangeOptionItem.svelte';
	import ConnectWalletButton from '$lib/util/ConnectWalletButton.svelte';
	import {
		loadingUserPortfolioPoints,
		portfolioChange24H,
		portfolioChange24HPercentage,
		portfolioValue,
		userPortfolioPoints
	} from '$lib/portfolio/store.js';
	import { firstLoadedUserBalances, userBalances } from '$lib/home/store.js';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import { fade } from 'svelte/transition';
	import { quadInOut } from 'svelte/easing';
	import { firstLoadedMargin, sumOfAllUserMargin } from '$lib/home/userInteract/store';
	import CreateScWalletButtonWithInfo from '$lib/util/CreateSCWalletButtonWithInfo.svelte';

	$: portfolioChangePositive = new BigNumber($portfolioChange24H).gte(0);
</script>

<div class="text-fx-gradient-5 font-semibold text-lg hidden sm:block sm:text-2lg">
	Portfolio Value
</div>
{#if $walletLoaded && $walletConnected}
	<div class="">
		<div class="text-fx-gradient-20 font-semibold text-3xl sm:hidden text-left">
			{#if $userBalances && $smartContractWalletAddress && $sumOfAllUserMargin && $userBalances.sUSD && $firstLoadedMargin && $firstLoadedUserBalances}
				<Tooltip
					title="Includes all assets in your smart contract wallet and sum of total margin in all markets. Includes fees."
					header="Total Portfolio Value"
				>
					{addDollarSign(getNumBackInUnits($portfolioValue, 18, 2))}
				</Tooltip>
			{:else}
				<div class="flex items-center">
					<div class="mr-2">$</div>
					<div
						class="animate-pulse bg-fx-grey-800 h-10 col-span-2 rounded-lg squircle-lg w-16 mx-auto"
					/>
				</div>
			{/if}
		</div>
		{#if $portfolioChange24H && $portfolioChange24HPercentage && !new BigNumber($portfolioChange24H).eq(0)}
			<div class="flex sm:hidden justify-center items-center px-1.5 sm:px-2.5 py-1 sm:py-2.5 gap-2">
				<div class="text-fx-gradient-5 text-2xl font-semibold">
					{addDollarSign(getNumBackInUnits($portfolioChange24H, 18, 2))}
				</div>

				<div
					class="{portfolioChangePositive
						? 'text-fx-gradient-23'
						: 'text-fx-gradient-22'}  text-lg font-semibold"
				>
					({$portfolioChange24HPercentage}%)
				</div>

				<div class="text-fx-grey-400 text-xs">24h</div>
			</div>
		{/if}
	</div>
{/if}
<div class="border-t border-t-fx-grey-600 w-full sm:my-5 my-2 hidden sm:block" />

<svelte:head>
	<link rel="preload" as="image" href="connect-wallet.webp" />
</svelte:head>

{#if $walletLoaded && !$walletConnected}
	<div class="w-full">
		<div class="flex flex-col items-center">
			<img
				loading="lazy"
				src={'connect-wallet.webp'}
				alt="connect wallet"
				class="pt-14 max-h-44 animate-pulse m-10 sm:m-0"
				in:fade|global={{
					duration: 500,
					delay: 200,
					easing: quadInOut
				}}
			/>
			<div class="pt-3 w-full sm:w-96">
				<ConnectWalletButton />
			</div>
			<div class="text-fx-grey-400 text-xs sm:text-sm pt-3 mb-16 text-center">
				Connect your Optimism wallet to deposit funds & start trading.
			</div>
		</div>
	</div>
{:else if $smartWalletCreationPending}
	<div class="w-full">
		<CreateScWalletButtonWithInfo source="portfolio" />
	</div>
{:else if $walletLoaded && $walletConnected && $smartContractWalletAddress}
	<div class="w-full">
		<div class="flex justify-between items-start">
			<div class="flex flex-col sm:flex-row items-start justify-start sm:gap-2 mb-4">
				<div class="text-fx-gradient-20 font-semibold text-4xl hidden sm:block ml-2 sm:ml-0">
					{#if $userBalances && $sumOfAllUserMargin && $userBalances.sUSD && $firstLoadedMargin && $firstLoadedUserBalances}
						<Tooltip
							title="Includes all assets in your smart contract wallet and sum of total margin in all markets. Includes fees."
							header="Total Portfolio Value"
						>
							{addDollarSign(getNumBackInUnits($portfolioValue, 18, 2))}
						</Tooltip>
					{:else}
						<div class="flex items-center">
							<div
								class="animate-pulse bg-fx-grey-800 h-10 col-span-2 rounded-lg squircle-lg w-20 mx-auto"
							/>
						</div>
					{/if}
				</div>
				{#if !new BigNumber($portfolioChange24H).isNaN() && !new BigNumber($portfolioChange24HPercentage).isNaN()}
					<GradientBorder
						gradient="bg-fx-gradient-6"
						paddingClass="p-px"
						borderRadius="rounded-lg squircle-lg"
						outerClassList="mb-2 mt-2"
						innerClassList="hidden sm:flex justify-center items-center bg-fx-black px-1.5 sm:px-2.5 py-1 sm:py-2.5 gap-2.5"
					>
						<div class="text-fx-gradient-5 text-xs sm:text-lg font-semibold">
							{addDollarSign(getNumBackInUnits($portfolioChange24H, 18, 2))}
						</div>
						{#if Math.abs(Number($portfolioChange24HPercentage)) !== Number.POSITIVE_INFINITY}
							<div
								class="{portfolioChangePositive
									? 'text-fx-gradient-23'
									: 'text-fx-gradient-22'}  text-xs sm:text-lg font-semibold"
							>
								{!new BigNumber($portfolioChange24H).eq(0) ? $portfolioChange24HPercentage : '0'}%
							</div>
						{/if}
						<div class="text-fx-grey-400 text-xs">Past day</div>
					</GradientBorder>
				{:else}
					<div
						class="animate-pulse h-10 bg-fx-grey-700 w-32 rounded-lg squircle-lg dark:bg-fx-grey-800 ml-1"
					/>
				{/if}
			</div>
			<div
				class="flex text-fx-grey-200 text-xs xl:gap-5 gap-2 bg-fx-grey-900 sm:py-3 py-1 sm:px-3 px-1.5 rounded-lg squircle-lg w-fit ml-auto"
			>
				<ChartRangeOptionItem range="1W" />
				<ChartRangeOptionItem range="1M" />
				<ChartRangeOptionItem range="ALL" />
			</div>
		</div>
		{#if !$userPortfolioPoints?.length || $userPortfolioPoints.length < 1}
			{#if $loadingUserPortfolioPoints}
				<div class="animate-pulse h-96 bg-fx-grey-700 w-full rounded-lg squircle-lg" />
			{:else}
				<div class="w-full">
					<div class="flex flex-col items-center">
						<img loading="lazy" src={'no-trades.webp'} alt="no trade" class="pt-8 max-h-40" />
						<div class="text-fx-grey-200 font-medium text-sm pt-3">
							Here you can track your portfolio.
						</div>
						<div class="text-xs pt-2 mb-16 mx-10 text-center">
							<span class="text-fx-grey-400"
								>We will show you a detailed graph here when we have more data. Place your trade</span
							>
							<a href="/perps" class="text-fx-pink">here.</a>
						</div>
					</div>
				</div>
			{/if}
		{:else}
			<Chart gradientLineLength={400} />
		{/if}
	</div>
{:else}
	<div class="animate-pulse h-full bg-fx-grey-700 w-full rounded-lg squircle-lg" />
{/if}
